// src/pages/ResetPassword.jsx
import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import Swal from "sweetalert2";
import { loadingToggleAction } from "../../store/actions/AuthActions";

import bg6 from "../../images/background/nye-big-800.png";

const API_BASE_URL = process.env.REACT_APP_PREDICTIONS_API;

export default function ResetPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search] = useSearchParams();

  const initialToken = search.get("code") || search.get("token") || "";

  const [resetToken, setResetToken] = useState(initialToken);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const errors = useMemo(() => {
    if (!submitted) return {};

    const e = {};

    if (!resetToken.trim()) {
      e.resetToken = "Unique code is required.";
    }

    if (!newPassword) {
      e.newPassword = "Password is required.";
    } else if (newPassword.length < 8) {
      e.newPassword = "Password must be at least 8 characters.";
    }

    if (!confirmPassword) {
      e.confirmPassword = "Confirm password is required.";
    } else if (confirmPassword.length < 8) {
      e.confirmPassword = "Confirm password must be at least 8 characters.";
    } else if (newPassword !== confirmPassword) {
      e.confirmPassword = "Passwords do not match.";
    }

    return e;
  }, [submitted, resetToken, newPassword, confirmPassword]);

  async function handleResetPassword(e) {
    e.preventDefault();
    setSubmitted(true);

    const nextErrors = {};

    if (!resetToken.trim()) {
      nextErrors.resetToken = "Unique code is required.";
    }

    if (!newPassword) {
      nextErrors.newPassword = "Password is required.";
    } else if (newPassword.length < 8) {
      nextErrors.newPassword = "Password must be at least 8 characters.";
    }

    if (!confirmPassword) {
      nextErrors.confirmPassword = "Confirm password is required.";
    } else if (confirmPassword.length < 8) {
      nextErrors.confirmPassword =
        "Confirm password must be at least 8 characters.";
    } else if (newPassword !== confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match.";
    }

    try {
      setSubmitting(true);
      dispatch(loadingToggleAction(true));

      await axios.post(`${API_BASE_URL}/api/forgot-password`, {
        password: newPassword,
        uniqueCode: resetToken,
      });

      const res = await Swal.fire({
        icon: "success",
        title: "Password updated successfully",
        confirmButtonText: "OK",
      });

      if (res.isConfirmed) navigate("/login");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "An error occurred while resetting your password. Please try again later.";
      Swal.fire({
        icon: "error",
        title: "Password reset failed",
        text: msg,
      });
    } finally {
      setSubmitting(false);
      dispatch(loadingToggleAction(false));
    }
  }

  return (
    <div
      className="d-flex justify-content-center align-items-center min-vh-100 bg-img-fix overflow-hidden"
      style={{ background: `#fff url(${bg6}) center center / cover no-repeat` }}
    >
      <div
        className="card shadow-sm"
        style={{ width: "100%", maxWidth: "420px" }}
      >
        <div className="card-body">
          <h3 className="text-center mb-4">Reset Password</h3>

          <form onSubmit={handleResetPassword} noValidate>
            <div className="mb-3">
              <label htmlFor="uniqueCode" className="form-label fw-semibold">
                Unique Code
              </label>
              <input
                type="text"
                id="uniqueCode"
                className={`form-control ${
                  errors.resetToken ? "is-invalid" : ""
                }`}
                value={resetToken}
                onChange={(e) => setResetToken(e.target.value)}
                placeholder="Enter or paste your unique code"
                autoComplete="one-time-code"
              />
              {errors.resetToken && (
                <div className="invalid-feedback">{errors.resetToken}</div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="newPassword" className="form-label fw-semibold">
                New Password
              </label>
              <div className="input-group">
                <input
                  type="text"
                  id="newPassword"
                  className={`form-control ${
                    errors.newPassword ? "is-invalid" : ""
                  }`}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
              </div>
              {errors.newPassword && (
                <div className="invalid-feedback d-block">
                  {errors.newPassword}
                </div>
              )}
            </div>

            <div className="mb-4">
              <label
                htmlFor="confirmPassword"
                className="form-label fw-semibold"
              >
                Confirm Password
              </label>
              <input
                type="text"
                id="confirmPassword"
                className={`form-control ${
                  errors.confirmPassword ? "is-invalid" : ""
                }`}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                autoComplete="new-password"
              />
              {errors.confirmPassword && (
                <div className="invalid-feedback">{errors.confirmPassword}</div>
              )}
            </div>

            <div className="d-grid">
              <button
                type="submit"
                className="btn btn-primary"
              >
                Update Password
              </button>
            </div>

            <div className="text-center mt-3">
              <a href="/login" className="text-decoration-none">
                Continue to login
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
